export interface Translations {
    [key: string]: {
        pageTitle: string;
        mainTitle: string;
        subtitle: string;
        namePlaceholder: string;
        descPlaceholder: string;
        button: string;
        alert: string;
        descriptionTitle: string;
        useAIGeneration: string;
        analysis: {
            awakening_phrases: string[];
            motivations: {
                sacrifice: string;
                hatred: string;
                protection: string;
                loss: string;
                love: string;
                revelation: string;
                duty: string;
                legacy: string;
                generic: string;
            };
            keywords: {
                sacrifice: string[];
                hatred: string[];
                protection: string[];
                loss: string[];
                love: string[];
                revelation: string[];
                duty: string[];
                legacy: string[];
            };
            design_connection: string;
            power_intro: string;
            powers: { name: string; desc: string }[];
        };
        promptPart1: string;
        promptPart2: string;
        promptPart3: string;
    };
}

export const translations: Translations = {
    fr: { 
        pageTitle: "Générateur de Mangekyō Sharingan", 
        mainTitle: "Sharingan Forge", 
        subtitle: "Le nom et l'histoire de votre personnage forgeront sa pupille.", 
        namePlaceholder: "Nom du personnage...", 
        descPlaceholder: "Ex: Le sacrifice d'un être cher pour protéger le village...", 
        button: "Éveiller le Sharingan", 
        alert: "Veuillez entrer un nom ou une description pour éveiller le Mangekyō Sharingan.", 
        descriptionTitle: "Analyse de la Pupille",
        useAIGeneration: "Utiliser l'IA pour la description",
        analysis: {
            awakening_phrases: ["Ce Mangekyō s'est éveillé suite à une vague d'émotions d'une puissance rare, marquant l'âme de son porteur à jamais.", "La pupille s'est ouverte suite à un événement bouleversant qui a changé le cours de sa vie.", "C'est dans un océan d'émotions pures que ce dōjutsu a finalement vu le jour."],
            motivations: {
                sacrifice: "Né du sacrifice, il reflète une volonté inébranlable de protéger les autres, quel qu'en soit le prix.",
                hatred: "Forgé dans la haine et la soif de vengeance, il est le miroir d'une âme tourmentée cherchant une justice sanglante.",
                protection: "Son pouvoir vient d'un désir ardent de protéger un être cher, une promesse gravée dans le sang et la détermination.",
                loss: "La perte irréparable d'un ami ou d'un mentor a déclenché ce pouvoir maudit, alimenté par le regret et la solitude.",
                love: "Éveillé par un amour profond et inconditionnel, ce Sharingan symbolise un lien puissant et le désir de chérir ses proches.",
                revelation: "La vérité a été une lame à double tranchant. C'est le choc de la mémoire retrouvée et la redécouverte de son identité qui ont donné naissance à ce Mangekyō.",
                duty: "Éveillé par le sens du devoir, ce dōjutsu symbolise la responsabilité envers sa famille et son village, une charge acceptée malgré le statut de paria.",
                legacy: "Ce Sharingan est le reflet d'un héritage lourd de conséquences, une pupille qui porte le poids des générations passées et d'une mission confiée par un père.",
                generic: "Les émotions ressenties lors de cet événement ont gravé ce motif unique dans ses yeux."
            },
            keywords: {
                sacrifice: ['sacrifice', 'sacrifié', 'donner sa vie'],
                hatred: ['haine', 'vengeance', 'trahison', 'colère', 'rage'],
                protection: ['protéger', 'sauver', 'défendre'],
                loss: ['perte', 'perdu', 'mort', 'mourir', 'ami', 'mentor'],
                love: ['amour', 'aimer', 'joie', 'bonheur', 'proches', 'famille'],
                revelation: ['mémoire', 'rappelé', 'récupère', 'apprit', 'lettre', 'vérité'],
                duty: ['mission', 'ramener', 'konoha', 'fille', 'occuper', 'enseigner', 'devoir'],
                legacy: ['père', 'mère', 'oncle', 'gunbai', 'madara', 'parchemin', 'héritage']
            },
            design_connection: "Le design de cette pupille, ",
            power_intro: "Ce dōjutsu confère des pouvoirs uniques, reflets des émotions de son âme :",
            powers: [
                { name: "Amaterasu", desc: "les flammes noires inextinguibles qui consument tout sur leur passage." },
                { name: "Tsukuyomi", desc: "un genjutsu dévastateur pouvant altérer la perception du temps et de l'espace dans un monde illusoire." },
                { name: "Kotoamatsukami", desc: "le genjutsu ultime de manipulation de la pensée, capable de changer les convictions d'une cible sans qu'elle s'en aperçoive." },
                { name: "Kamui", desc: "une technique spatio-temporelle permettant de téléporter des objets ou soi-même dans une autre dimension." },
                { name: "Susanoo", desc: "l'incarnation d'une puissance défensive et offensive absolue, un titan de chakra." },
                { name: "Shinra Tensei", desc: "une technique unique de manipulation des forces répulsives, capable de repousser toute attaque." },
                { name: "Kyōka Suigetsu", desc: "un genjutsu sonore capable de piéger ses cibles dans une illusion auditive parfaite." }
            ]
        },
        promptPart1: "En tant qu'expert de l'univers Naruto, rédige une description pour un nouveau Mangekyō Sharingan. Voici l'histoire tragique de son éveil : ", 
        promptPart2: "\n\nSon design est ", 
        promptPart3: "\n\nEn te basant sur cette histoire et ce design, décris les valeurs et les objectifs du personnage, ainsi que les pouvoirs uniques que ce Mangekyō lui confère (Amaterasu, Tsukuyomi, Kamui, Kotoamatsukami, Susanoo, ou une technique originale). La description doit être immersive, stylisée et ne doit pas dépasser 500 caractères."
    },
    // ... (les autres langues peuvent être ajoutées ici sur le même modèle)
};

export interface PatternStyles {
    [key: string]: string[];
}

export const patternStyles: PatternStyles = {
    fr: ["complexe et en forme de faux", "en forme d'étoile à plusieurs branches, rappelant celui de Sasuke ou Madara", "semblable à un pétale de lotus, à la fois élégant et mortel", "doté de faux acérées et incurvées", "large et rayonnant, comme un soleil aux lames acérées", "composé de larges lames tourbillonnantes, très similaire à celui de Shisui Uchiha", "un design inversé, avec des motifs rouges sur un iris noir, semblable à celui de Sasuke Uchiha"],
    // ... (les autres langues)
};